/**
 * FileList Component
 * ==================
 * Display uploaded files with status indicators, download, and remove options.
 */

import React from 'react';
import { UploadedFile } from '../index';
import documentApi from '../../../services/documentApi';

interface FileListProps {
    files: UploadedFile[];
    onRemove: (id: string) => void;
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
        case 'uploading':
        case 'processing':
            return (
                <span className="file-list__spinner" title={status === 'uploading' ? 'Uploading...' : 'Processing OCR...'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" opacity="0.3" />
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 12 12"
                                to="360 12 12"
                                dur="1s"
                                repeatCount="indefinite"
                            />
                        </path>
                    </svg>
                </span>
            );
        case 'done':
            return (
                <span className="file-list__status-done" title="OCR completed">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </span>
            );
        case 'error':
            return (
                <span className="file-list__status-error" title="Error">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </span>
            );
        default: // pending
            return null;
    }
};

const FileList: React.FC<FileListProps> = ({ files, onRemove }) => {
    if (files.length === 0) {
        return null;
    }

    const handleDownload = async (file: UploadedFile) => {
        if (!file.serverDocId) return;
        try {
            await documentApi.downloadDocument(file.serverDocId, file.name);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    return (
        <div className="file-list">
            {files.map((file) => (
                <div key={file.id} className={`file-list__item file-list__item--${file.status}`}>
                    <div
                        className="file-list__clickable"
                        onClick={() => handleDownload(file)}
                        title="Click to download"
                        style={{ cursor: file.serverDocId ? 'pointer' : 'default' }}
                    >
                        <div className="file-list__icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                        </div>
                        <div className="file-list__info">
                            <span className="file-list__name">{file.name}</span>
                            <span className="file-list__meta">
                                {formatFileSize(file.size)}
                                {file.error && <span className="file-list__error"> - {file.error}</span>}
                            </span>
                        </div>
                    </div>
                    <div className="file-list__status">
                        {getStatusIcon(file.status)}
                    </div>
                    <button
                        className="file-list__remove"
                        onClick={() => onRemove(file.id)}
                        title="Remove file"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            ))}
        </div>
    );
};

export default FileList;
